import { dayjs } from "@volca/ui/lib/date";
/** Which end of the range a typed edit is aimed at. */
export const RANGE_END = { from: "from", to: "to" } as const;
export type RangeEnd = (typeof RANGE_END)[keyof typeof RANGE_END];

/** How many months the calendar shows at once. */
export const MONTHS_ON_SCREEN = 2;

/**
 * Which month the calendar should show after a date is typed.
 *
 * Typing the start simply goes there. Typing the end keeps the whole range on
 * screen when it fits in the months on offer, because seeing a range's start
 * disappear the moment you finish typing its end reads as the picker losing
 * your work. Only a range too long to fit gives up the start, and then the end
 * you just typed is the half worth showing.
 */
export const anchorMonth = (
  end: RangeEnd,
  typed: Date,
  range: { from?: Date; to?: Date },
  monthsOnScreen = MONTHS_ON_SCREEN,
) => {
  if (end === RANGE_END.from || !range.from) return typed;

  // Ordered, because someone can type an end that falls before the start, and
  // the range is swapped on apply anyway.
  const reversed = dayjs(typed).isBefore(range.from);
  const start = reversed ? typed : range.from;
  const finish = reversed ? range.from : typed;

  // Whole months, not raw dates: Jan 31 to Mar 1 is under two months apart but
  // still needs three months of calendar to show both ends.
  const monthsSpanned = dayjs(finish)
    .startOf("month")
    .diff(dayjs(start).startOf("month"), "month");

  return monthsSpanned < monthsOnScreen
    ? start
    : dayjs(typed)
        .subtract(monthsOnScreen - 1, "month")
        .toDate();
};

export const createDateLabel = (from: Date, to: Date) => {
  const currentYear = dayjs().year();
  const fromYear = dayjs(from).year();
  const toYear = dayjs(to).year();

  const formatString =
    fromYear === currentYear && toYear === currentYear
      ? "MMM D"
      : "MMM D, YYYY";

  // Check if it's the same day (single day selection)
  const isSameDay = dayjs(from).isSame(to, "day");

  return isSameDay
    ? dayjs(from).format(formatString)
    : `${dayjs(from).format(formatString)} - ${dayjs(to).format(formatString)}`;
};
